/**
 * GA4 Analytics — full instrumentation with Consent Mode v2, User-ID,
 * user properties, SPA page context persistence, and dedup.
 *
 * Enhanced Measurement (auto-tracked by GA4 — do NOT duplicate):
 *   scroll, click (outbound), file_download, form_start, form_submit
 *
 * GA4 limits to respect:
 *   Event names: max 40 chars, snake_case, no ga_/google_/firebase_ prefix
 *   Params per event: max 25
 *   Param name: max 40 chars
 *   Param value: max 100 chars (page_title 300, page_location 1000)
 *   User property name: max 24 chars, value: max 36 chars
 */

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

const GA4_ID = import.meta.env.VITE_GA4_ID as string | undefined
const IS_DEV = import.meta.env.DEV

let _initialized = false

// ── Page context (persisted across SPA navigations) ─────────────────

export let currentPageContext = {
  page_location: typeof window !== 'undefined' ? window.location.href : '',
  page_title: typeof document !== 'undefined' ? document.title : '',
}

export function updatePageContext(path: string, title?: string): void {
  currentPageContext = {
    page_location: (typeof window !== 'undefined' ? window.location.origin : '') + path,
    page_title: title || (typeof document !== 'undefined' ? document.title : ''),
  }
}

// ── Consent Mode v2 ─────────────────────────────────────────────────

function ensureGtag(): void {
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
  }
}

/** Must be called BEFORE initGA4 — sets default-denied consent signals. */
export function setConsentDefaults(): void {
  ensureGtag()
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  })
}

/** Called when user makes a consent choice in the cookie banner. */
export function updateConsent(analytics: boolean, marketing: boolean): void {
  ensureGtag()
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  })
  if (analytics) initGA4()
}

// ── Initialization ──────────────────────────────────────────────────

export function initGA4(): void {
  if (_initialized || !GA4_ID) return
  _initialized = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`
  document.head.appendChild(script)

  ensureGtag()
  window.gtag('js', new Date())
  window.gtag('config', GA4_ID, {
    send_page_view: false,
    ...(IS_DEV && { debug_mode: true }),
  })
}

export function isGA4Initialized(): boolean {
  return _initialized
}

// ── User-ID (cross-device tracking) ────────────────────────────────

export function setGA4UserId(userId: string): void {
  if (typeof window.gtag !== 'function' || !GA4_ID) return
  window.gtag('config', GA4_ID, { user_id: userId })
}

export function clearGA4UserId(): void {
  if (typeof window.gtag !== 'function' || !GA4_ID) return
  window.gtag('config', GA4_ID, { user_id: null })
}

// ── User Properties (user-scoped custom dimensions) ────────────────

export function setGA4UserProperties(props: {
  user_role?: string
  user_plan?: string
  has_team?: string
  signup_method?: string
}): void {
  if (typeof window.gtag !== 'function') return
  window.gtag('set', 'user_properties', props)
}

// ── Internal event dispatcher with dedup + page context ─────────────

const _lastEvent = { name: '', time: 0 }

function event(name: string, params?: Record<string, unknown>): void {
  if (typeof window.gtag !== 'function') return

  const now = Date.now()
  if (name === _lastEvent.name && now - _lastEvent.time < 500) return
  _lastEvent.name = name
  _lastEvent.time = now

  if (IS_DEV) validateEvent(name, params)

  window.gtag('event', name, {
    ...currentPageContext,
    ...params,
  })
}

function validateEvent(name: string, params?: Record<string, unknown>): void {
  if (name.length > 40) console.warn(`[GA4] Event name exceeds 40 chars: "${name}"`)
  if (name.startsWith('ga_') || name.startsWith('google_') || name.startsWith('firebase_'))
    console.warn(`[GA4] Reserved event name prefix: "${name}"`)
  if (!params) return
  const keys = Object.keys(params)
  if (keys.length > 25) console.warn(`[GA4] Event "${name}" has ${keys.length} params (max 25)`)
  for (const k of keys) {
    if (k.length > 40) console.warn(`[GA4] Param name exceeds 40 chars: "${k}"`)
    const v = params[k]
    if (typeof v === 'string' && v.length > 100)
      console.warn(`[GA4] Param "${k}" value exceeds 100 chars (${v.length})`)
  }
}

// ── Navigation ──────────────────────────────────────────────────────

export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (typeof window.gtag !== 'function') return
  const loc = (typeof window !== 'undefined' ? window.location.origin : '') + pagePath
  window.gtag('event', 'page_view', {
    page_location: loc,
    page_title: pageTitle || document.title,
    page_path: pagePath,
  })
}

// ── GA4 Recommended Events ──────────────────────────────────────────
// Use exact GA4 names to unlock built-in reporting & predictive audiences.

export function trackSignup(method: string = 'email'): void {
  event('sign_up', { method })
}

export function trackLogin(method: string = 'otp'): void {
  event('login', { method })
}

export function trackTutorialBegin(): void {
  event('tutorial_begin')
}

export function trackTutorialComplete(role?: string): void {
  event('tutorial_complete', role ? { role } : undefined)
}

export function trackGenerateLead(params?: { value?: number; currency?: string }): void {
  event('generate_lead', params)
}

export function trackJoinGroup(groupId: string): void {
  event('join_group', { group_id: groupId })
}

export function trackShare(method: string, contentType: string, itemId: string): void {
  event('share', { method, content_type: contentType, item_id: itemId })
}

export function trackSearch(searchTerm: string): void {
  if (!searchTerm.trim()) return
  event('search', { search_term: searchTerm.slice(0, 100) })
}

export function trackSelectContent(contentType: string, contentId: string): void {
  event('select_content', { content_type: contentType, content_id: contentId })
}

// ── Custom Events: Funnel ───────────────────────────────────────────

export function trackLandingCtaClick(ctaLabel: string, destination: string): void {
  event('landing_cta_click', { cta_label: ctaLabel, destination })
}

export function trackSignupStarted(method: string, source: string): void {
  event('signup_started', { method, source })
}

export function trackCoreActivationCompleted(role: string): void {
  event('core_activation_completed', { role })
}

// ── Custom Events: Team ─────────────────────────────────────────────

export function trackInviteSent(role: string, teamId?: string): void {
  event('invite_sent', { role, ...(teamId && { team_id: teamId }) })
}

export function trackTeamMemberJoined(role: string): void {
  event('team_member_joined', { role })
}

// ── Custom Events: Shows ────────────────────────────────────────────

export function trackShowCreated(source: 'manual' | 'bandsintown' = 'manual'): void {
  event('show_created', { source })
}

export function trackTicketUpdate(showId: string, ticketCount: number): void {
  event('ticket_update', { show_id: showId, ticket_count: ticketCount })
}

export function trackShowSynced(count: number): void {
  event('show_synced', { count })
}

// ── Custom Events: Music ────────────────────────────────────────────

export function trackSongAdded(source: 'manual' | 'spotify' = 'manual'): void {
  event('song_added', { source })
}

// ── Custom Events: Links ────────────────────────────────────────────

export function trackLinkPublished(linkType: string, linkId?: string): void {
  event('link_published', { link_type: linkType, ...(linkId && { link_id: linkId }) })
}

export function trackLinkClick(linkId: string, linkType: string): void {
  event('link_click', { link_id: linkId, link_type: linkType })
}

export function trackLinkSubmission(linkId: string): void {
  event('link_submission', { link_id: linkId })
}

export function trackPublicLinkViewed(linkType: string, linkId?: string): void {
  event('public_link_viewed', { link_type: linkType, ...(linkId && { link_id: linkId }) })
}

// ── Custom Events: Guestlist ────────────────────────────────────────

export function trackGuestlistCreated(guestlistId?: string): void {
  event('guestlist_created', guestlistId ? { guestlist_id: guestlistId } : undefined)
}

export function trackGuestAdded(guestlistId: string, guestCount: number): void {
  event('guest_added', { guestlist_id: guestlistId, guest_count: guestCount })
}

export function trackGuestlistInviteSent(guestlistId: string): void {
  event('guestlist_invite_sent', { guestlist_id: guestlistId })
}

export function trackGuestlistRequestSubmitted(guestlistSlug: string): void {
  event('guestlist_request_submitted', { guestlist_slug: guestlistSlug })
}

export function trackGuestlistRequestReviewed(guestlistId: string, status: string): void {
  event('guestlist_request_reviewed', { guestlist_id: guestlistId, review_status: status })
}

// ── Custom Events: Money / Finance ──────────────────────────────────

export function trackMoneyViewed(): void {
  event('money_viewed')
}

export function trackMoneyFilterApplied(filterType: string, filterValue: string): void {
  event('money_filter_applied', { filter_type: filterType, filter_value: filterValue })
}

export function trackMoneyEntry(type: 'revenue' | 'spend', amount: number, source: string): void {
  event('money_entry', { type, amount, source })
}

// ── Custom Events: Tasks ────────────────────────────────────────────

export function trackTaskCreated(category?: string): void {
  event('task_created', { category })
}

export function trackTaskCompleted(category?: string): void {
  event('task_completed', { category })
}

// ── Custom Events: Chat / AI ────────────────────────────────────────

export function trackChatMessage(): void {
  event('chat_message')
}

// ── Custom Events: Engagement ───────────────────────────────────────

export function trackFeatureUsed(feature: string, detail?: string): void {
  event('feature_used', { feature, ...(detail && { detail }) })
}

// ── Custom Events: Error Observability ──────────────────────────────

export function trackAppError(errorMessage: string, componentStack?: string): void {
  event('app_error', {
    error_message: errorMessage.slice(0, 100),
    ...(componentStack && { component_stack: componentStack.slice(0, 100) }),
  })
}

export function trackApiError(endpoint: string, statusCode: number, errorMessage?: string): void {
  event('api_error', {
    api_endpoint: endpoint.slice(0, 100),
    status_code: statusCode,
    ...(errorMessage && { error_message: errorMessage.slice(0, 100) }),
  })
}
